import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { 
  MessageSquare, 
  Send, 
  CheckCircle, 
  HelpCircle,
  FileText,
  Calendar,
  Award,
  User,
  Settings,
  CreditCard,
  AlertCircle
} from 'lucide-react';

interface UserProfile {
  full_name: string;
  email: string;
  organization: string;
  position: string;
}

interface GeneralEnquiryFormProps {
  userProfile: UserProfile;
  onEnquirySubmitted?: () => void;
}

const GeneralEnquiryForm = ({ userProfile, onEnquirySubmitted }: GeneralEnquiryFormProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [enquiryType, setEnquiryType] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState('normal');
  const { toast } = useToast();

  const enquiryTypes = [
    { value: 'resources', label: 'Resources', icon: FileText, description: 'Questions about available resources and materials' },
    { value: 'events', label: 'Events', icon: Calendar, description: 'Enquiries about upcoming events and registration' },
    { value: 'achievements', label: 'Achievements', icon: Award, description: 'Questions about badges, certificates, and achievements' },
    { value: 'profile', label: 'Profile', icon: User, description: 'Account and profile related enquiries' },
    { value: 'membership', label: 'Membership', icon: CreditCard, description: 'Membership plans, billing, and benefits' },
    { value: 'technical', label: 'Technical', icon: Settings, description: 'Technical issues and platform support' },
    { value: 'general', label: 'General', icon: HelpCircle, description: 'General questions and other enquiries' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-800' },
    { value: 'normal', label: 'Normal', color: 'bg-blue-100 text-blue-800' },
    { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-800' },
    { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-800' }
  ];

  const handleSubmit = async () => {
    if (!enquiryType || !subject.trim() || !message.trim()) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { data, error } = await supabase
        .from('general_enquiries_2025_12_03_13_00')
        .insert([
          {
            user_email: userProfile.email,
            user_name: userProfile.full_name,
            enquiry_type: enquiryType,
            subject: subject.trim(),
            message: message.trim(),
            priority: priority,
            status: 'pending'
          }
        ])
        .select();

      if (error) {
        console.error('Error submitting enquiry:', error);
        throw new Error(error.message);
      }

      console.log('Enquiry submitted successfully:', data);
      
      setIsSubmitted(true);
      onEnquirySubmitted?.();
      
      toast({
        title: "Enquiry Submitted!",
        description: "Your enquiry has been sent to the admin team. You'll receive a response soon.",
      });

    } catch (error) {
      console.error('Error submitting enquiry:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast({
        title: "Submission Failed",
        description: `Unable to submit enquiry: ${errorMessage}. Please try again.`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setIsSubmitted(false);
    setEnquiryType('');
    setSubject('');
    setMessage('');
    setPriority('normal');
    setIsOpen(false);
  };

  const selectedType = enquiryTypes.find(type => type.value === enquiryType);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className="w-full" onClick={() => setIsOpen(true)}>
          <MessageSquare className="h-4 w-4 mr-2" />
          Submit General Enquiry
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            General Enquiry Form
          </DialogTitle>
          <DialogDescription>
            Submit your questions or concerns and our admin team will respond promptly
          </DialogDescription>
        </DialogHeader>

        {!isSubmitted ? (
          <div className="space-y-6">
            {/* User Information Display */}
            <Card className="bg-gray-50">
              <CardHeader>
                <CardTitle className="text-sm">Your Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Name:</span> {userProfile.full_name}
                  </div>
                  <div>
                    <span className="font-medium">Email:</span> {userProfile.email}
                  </div>
                  <div>
                    <span className="font-medium">Organization:</span> {userProfile.organization || 'Not specified'}
                  </div>
                  <div>
                    <span className="font-medium">Position:</span> {userProfile.position || 'Not specified'}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Enquiry Type Selection */}
            <div className="space-y-3">
              <Label htmlFor="enquiry-type">Enquiry Type *</Label>
              <Select value={enquiryType} onValueChange={setEnquiryType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select the type of enquiry" />
                </SelectTrigger>
                <SelectContent>
                  {enquiryTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          <div>
                            <div className="font-medium">{type.label}</div>
                            <div className="text-xs text-muted-foreground">{type.description}</div>
                          </div>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {selectedType && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <selectedType.icon className="h-4 w-4 text-blue-600" />
                  <span className="text-sm text-blue-700">{selectedType.description}</span>
                </div>
              )}
            </div>

            {/* Priority Selection */}
            <div className="space-y-3">
              <Label htmlFor="priority">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {priorityOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        <Badge className={option.color} variant="secondary">
                          {option.label}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="subject">Subject *</Label>
              <Input
                id="subject"
                placeholder="Brief description of your enquiry"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={200}
              />
              <div className="text-xs text-muted-foreground text-right">
                {subject.length}/200 characters
              </div>
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message">Message *</Label>
              <Textarea
                id="message"
                placeholder="Please provide detailed information about your enquiry..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                maxLength={2000}
              />
              <div className="text-xs text-muted-foreground text-right">
                {message.length}/2000 characters
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setIsOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={isSubmitting || !enquiryType || !subject.trim() || !message.trim()}
                className="flex-1"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit Enquiry
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          /* Success State */
          <div className="text-center py-8">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-green-800 mb-2">Enquiry Submitted Successfully!</h3>
            <p className="text-muted-foreground mb-6">
              Your enquiry about <strong>{selectedType?.label}</strong> has been sent to our admin team. 
              We'll respond to your enquiry at <strong>{userProfile.email}</strong> as soon as possible.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-700">
                <strong>What's next?</strong><br />
                Our team will review your enquiry and respond within 24-48 hours. 
                You can track the status of your enquiry in your dashboard.
              </p>
            </div>
            <Button onClick={resetForm} className="w-full">
              Submit Another Enquiry
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GeneralEnquiryForm;